import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { API, useOrg } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, FolderTree, Trash2, Edit2, X, Tag } from "lucide-react";

const CategoriesPage = () => {
  const { currentOrg } = useOrg();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form, setForm] = useState({ name: "", subcategories: [] });
  const [newSubcategory, setNewSubcategory] = useState("");

  const fetchCategories = async () => {
    if (!currentOrg) return;
    try {
      const response = await axios.get(`${API}/categories`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [currentOrg]);

  const resetForm = () => {
    setForm({ name: "", subcategories: [] });
    setNewSubcategory("");
    setEditingCategory(null);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setForm({ name: category.name, subcategories: [...(category.subcategories || [])] });
    setDialogOpen(true);
  };

  const addSubcategory = () => {
    const trimmed = newSubcategory.trim();
    if (!trimmed) return;
    if (form.subcategories.includes(trimmed)) {
      toast.error("Subcategory already exists");
      return;
    }
    setForm({ ...form, subcategories: [...form.subcategories, trimmed] });
    setNewSubcategory("");
  };

  const removeSubcategory = (index) => {
    setForm({ ...form, subcategories: form.subcategories.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Category name is required");
      return;
    }
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.category_id}`, form, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Category updated");
      } else {
        await axios.post(`${API}/categories`, form, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        toast.success("Category created");
      }
      await fetchCategories();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (categoryId) => {
    if (!confirm("Delete this category? Items using it won't be affected.")) return;
    try {
      await axios.delete(`${API}/categories/${categoryId}`, {
        withCredentials: true,
        headers: { 'X-Organization-Id': currentOrg.org_id }
      });
      toast.success("Category deleted");
      await fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  // Count items per category
  const [itemCounts, setItemCounts] = useState({});
  useEffect(() => {
    const fetchItems = async () => {
      if (!currentOrg) return;
      try {
        const res = await axios.get(`${API}/items`, {
          withCredentials: true,
          headers: { 'X-Organization-Id': currentOrg.org_id }
        });
        const counts = {};
        res.data.forEach(item => {
          const cat = item.category || '';
          if (cat) counts[cat] = (counts[cat] || 0) + 1;
        });
        setItemCounts(counts);
      } catch {}
    };
    fetchItems();
  }, [currentOrg, categories]);

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-medium text-zinc-900">Categories</h1>
          <p className="text-muted-foreground text-sm">Organize your items with categories and subcategories</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-category-btn">
              <Plus className="w-4 h-4" />
              Add Category
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingCategory ? "Edit Category" : "Create Category"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="cat-name">Category Name *</Label>
                <Input
                  id="cat-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Vape Products"
                  required
                  data-testid="category-name-input"
                />
              </div>

              {/* Subcategories */}
              <div>
                <Label>Subcategories</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newSubcategory}
                    onChange={(e) => setNewSubcategory(e.target.value)}
                    placeholder="Add subcategory"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubcategory(); } }}
                    data-testid="subcategory-input"
                  />
                  <Button type="button" variant="outline" onClick={addSubcategory} data-testid="add-subcategory-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {form.subcategories.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {form.subcategories.map((sub, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 text-sm"
                      >
                        <Tag className="w-3 h-3 text-muted-foreground" />
                        {sub}
                        <button
                          type="button"
                          onClick={() => removeSubcategory(i)}
                          className="text-muted-foreground hover:text-destructive ml-1"
                          data-testid={`remove-subcategory-${i}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="category-submit-btn">
                  {editingCategory ? "Update" : "Create"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full p-8 text-center text-muted-foreground">Loading...</div>
        ) : categories.length === 0 ? (
          <div className="col-span-full p-16 text-center">
            <FolderTree className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
            <p className="text-muted-foreground">No categories yet</p>
            <p className="text-xs text-muted-foreground mt-1">Create categories to organize your items</p>
          </div>
        ) : (
          categories.map((category) => (
            <div
              key={category.category_id}
              className="bg-white border border-zinc-200 p-5 hover:shadow-sm transition-shadow"
              data-testid={`category-card-${category.category_id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary/10 flex items-center justify-center rounded">
                    <FolderTree className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-zinc-900">{category.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {itemCounts[category.name] || 0} item{(itemCounts[category.name] || 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(category)} data-testid={`edit-category-${category.category_id}`}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(category.category_id)} className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {category.subcategories?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {category.subcategories.map((sub, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-50 border border-zinc-100 text-xs text-zinc-600">
                      <Tag className="w-2.5 h-2.5" />
                      {sub}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CategoriesPage;
